import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NAME } from 'src/common/constants/participantSortKeys';
import {
  applyParticipantListFilterToSearchParams,
  parseParticipantListSearchParams,
  type ParticipantListFilterPatch,
} from 'src/helpers/participantListQueryParams';
import {
  EParticipantFilters,
  type ParticipantFilterValue,
} from 'src/interfaces/Participant';
import { EOrder } from 'src/interfaces/Order';

interface UseParticipantListFiltersOptions {
  initialEditionId?: string | null;
  /** When true, filters + sort sync to the URL query string (admin participants). */
  persistInUrl?: boolean;
}

function asStringList(value: ParticipantFilterValue): string[] {
  if (value == null || value === '') {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function asScalar(value: ParticipantFilterValue): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return value;
}

export function useParticipantListFilters(options: UseParticipantListFiltersOptions = {}) {
  const { initialEditionId = null, persistInUrl = false } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  const hasInitializedEdition = useRef(false);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const [localQuery, setLocalQuery] = useState('');
  const [localCompetitionFilter, setLocalCompetitionFilter] = useState<string[]>([]);
  const [localDistanceFilter, setLocalDistanceFilter] = useState<string[]>([]);
  const [localCheckedInFilter, setLocalCheckedInFilter] = useState<string[]>([]);
  const [localRegisteredFrom, setLocalRegisteredFrom] = useState<string | null>(null);
  const [localRegisteredTo, setLocalRegisteredTo] = useState<string | null>(null);
  const [localEditionFilter, setLocalEditionFilter] = useState<string | null>(initialEditionId);
  const [localOwnerUserIdFilter, setLocalOwnerUserIdFilter] = useState<string | null>(null);
  const [localOrder, setLocalOrder] = useState<EOrder>(EOrder.asc);
  const [localOrderByKey, setLocalOrderByKey] = useState<string>(NAME);

  const parsed = useMemo(
    () => (persistInUrl ? parseParticipantListSearchParams(searchParams) : null),
    [persistInUrl, searchParams],
  );

  useEffect(() => {
    if (!persistInUrl || hasInitializedEdition.current) return;

    if (searchParams.has('editionId')) {
      hasInitializedEdition.current = true;
      return;
    }

    hasInitializedEdition.current = true;
    if (!initialEditionId) return;

    const next = applyParticipantListFilterToSearchParams(searchParamsRef.current, {
      editionFilter: initialEditionId,
    });
    searchParamsRef.current = next;
    setSearchParams(next, { replace: true });
  }, [persistInUrl, searchParams, initialEditionId, setSearchParams]);

  const query = persistInUrl ? (parsed?.query ?? '') : localQuery;
  const competitionFilter = persistInUrl
    ? (parsed?.competitionFilter ?? [])
    : localCompetitionFilter;
  const distanceFilter = persistInUrl ? (parsed?.distanceFilter ?? []) : localDistanceFilter;
  const checkedInFilter = persistInUrl ? (parsed?.checkedInFilter ?? []) : localCheckedInFilter;
  const registeredFrom = persistInUrl ? (parsed?.registeredFrom ?? null) : localRegisteredFrom;
  const registeredTo = persistInUrl ? (parsed?.registeredTo ?? null) : localRegisteredTo;
  const ownerUserIdFilter = persistInUrl
    ? (parsed?.ownerUserIdFilter ?? null)
    : localOwnerUserIdFilter;
  const order = persistInUrl ? (parsed?.order ?? EOrder.asc) : localOrder;
  const orderByKey = persistInUrl ? (parsed?.orderByKey ?? NAME) : localOrderByKey;

  const editionFilter = persistInUrl
    ? parsed?.editionFilter !== undefined
      ? parsed.editionFilter
      : hasInitializedEdition.current
        ? null
        : initialEditionId
    : localEditionFilter;

  const patchUrl = useCallback(
    (patch: ParticipantListFilterPatch) => {
      const next = applyParticipantListFilterToSearchParams(searchParamsRef.current, patch);
      searchParamsRef.current = next;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  const onChangeFilters = useCallback(
    (key: EParticipantFilters, value: ParticipantFilterValue) => {
      if (persistInUrl) {
        switch (key) {
          case EParticipantFilters.query:
            patchUrl({ query: asScalar(value) || '' });
            break;
          case EParticipantFilters.competition:
            // Distance pruning (invalid ids) is handled by the filter popover.
            patchUrl({ competitionFilter: asStringList(value) });
            break;
          case EParticipantFilters.distance:
            patchUrl({ distanceFilter: asStringList(value) });
            break;
          case EParticipantFilters.checkedIn:
            patchUrl({ checkedInFilter: asStringList(value) });
            break;
          case EParticipantFilters.registeredFrom:
            patchUrl({ registeredFrom: asScalar(value) });
            break;
          case EParticipantFilters.registeredTo:
            patchUrl({ registeredTo: asScalar(value) });
            break;
          case EParticipantFilters.edition:
            patchUrl({
              editionFilter: asScalar(value),
              competitionFilter: [],
              distanceFilter: [],
              // Clear period so a previous edition's window does not linger.
              registeredFrom: null,
              registeredTo: null,
            });
            break;
          case EParticipantFilters.ownerUserId:
            patchUrl({ ownerUserIdFilter: asScalar(value) });
            break;
          default:
            break;
        }
        return;
      }

      switch (key) {
        case EParticipantFilters.query:
          setLocalQuery(asScalar(value) || '');
          break;
        case EParticipantFilters.competition:
          setLocalCompetitionFilter(asStringList(value));
          break;
        case EParticipantFilters.distance:
          setLocalDistanceFilter(asStringList(value));
          break;
        case EParticipantFilters.checkedIn:
          setLocalCheckedInFilter(asStringList(value));
          break;
        case EParticipantFilters.registeredFrom:
          setLocalRegisteredFrom(asScalar(value));
          break;
        case EParticipantFilters.registeredTo:
          setLocalRegisteredTo(asScalar(value));
          break;
        case EParticipantFilters.edition:
          setLocalEditionFilter(asScalar(value));
          setLocalCompetitionFilter([]);
          setLocalDistanceFilter([]);
          setLocalRegisteredFrom(null);
          setLocalRegisteredTo(null);
          break;
        case EParticipantFilters.ownerUserId:
          setLocalOwnerUserIdFilter(asScalar(value));
          break;
        default:
          break;
      }
    },
    [persistInUrl, patchUrl],
  );

  const setOrder = useCallback(
    (next: EOrder) => {
      if (persistInUrl) {
        patchUrl({ order: next });
        return;
      }
      setLocalOrder(next);
    },
    [persistInUrl, patchUrl],
  );

  const setOrderByKey = useCallback(
    (next: string) => {
      if (persistInUrl) {
        patchUrl({ orderByKey: next });
        return;
      }
      setLocalOrderByKey(next);
    },
    [persistInUrl, patchUrl],
  );

  return {
    query,
    competitionFilter,
    distanceFilter,
    checkedInFilter,
    registeredFrom,
    registeredTo,
    editionFilter,
    ownerUserIdFilter,
    order,
    orderByKey,
    setOrder,
    setOrderByKey,
    onChangeFilters,
  };
}
